import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize, forkJoin, tap } from 'rxjs';
import { ApiError, HttpService } from '../../../services/http.service';
import { requiredTrimmed } from '../../../utils/utils';
import { InputComponent } from '../../from-input/input.component';
import { CustomToastComponent } from '../../toast/custom-toast.component';

export function dobNotInFuture(
  control: AbstractControl,
): ValidationErrors | null {
  if (!control.value) return null;

  const selectedDate = new Date(control.value);
  const today = new Date();

  selectedDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return selectedDate > today ? { futureDob: true } : null;
}
interface StudentDialogData {
  initialData?: APIResponse;
  EditMode: boolean;
  schoolID: string;
}

interface APIResponse {
  yearLevelId: any;
  ethnicityId: string;
  id: any;
  studentId: string;
  firstName: string;
  lastName: string;
  yearLevel: number;
  schoolEmail: string;
  dateOfBirth: string;
  personalEmail?: string;
  gender?: string;
  ethnicity?: string;
  parentName?: string;
  parentEmail?: string;
}

interface ApiDataResponse {
  status: boolean;
  message: string;
  data: APIResponse | APIResponse[];
}

@Component({
  selector: 'add-student',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputComponent, MatDialogModule],
  templateUrl: './add-teacher-student.component.html',
  styleUrls: ['./add-teacher-student.component.scss'],
})
export class AddTeacherStudentComponent implements OnInit {
  form!: FormGroup;
  isProcessing: boolean = false;
  isEdit: boolean = false;
  stepSubmit: boolean = false;
  showConsentSection: boolean = false;
  isUnder16: boolean = false;
  studentAge: number | null = null;
  dataOption: any[] = [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ];
  showConstructionRelatedCourseAnswers: boolean = false;
  showConstructionCourseAnswers: boolean = false;
  showPastAnswersError: boolean = false;
  showCompAnswersError: boolean = false;

  constructionAnswers: any[] = [
    {
      label:
        'Pre-Vocational (Introduction for Construction or Pre-Apprenticeship)',
      value:
        'Pre-Vocational (Introduction for Construction or Pre-Apprenticeship)',
    },
    {
      label: 'Certificate I (e.g. Cert I in Construction)',
      value: 'Certificate I (e.g. Cert I in Construction)',
    },
    {
      label: 'Certificate II (e.g. Cert II in Construction Pathways)',
      value: 'Certificate II (e.g. Cert II in Construction Pathways)',
    },
    {
      label: 'Certificate III (e.g. Cert III in Carpentry)',
      value: 'Certificate III (e.g. Cert III in Carpentry)',
    },
    {
      label: 'School-Based Apprenticeship or Traineeship (SBAT)',
      value: 'School-Based Apprenticeship or Traineeship (SBAT)',
    },
    { label: 'Unsure', value: 'Unsure' },
  ];

  genderOption: any[] = [
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female' },
    { label: 'Non-identifying', value: 'non-identifying' },
    { label: 'Prefer not to say', value: 'prefernottosay' },
  ];

  customErrorMessages = {
    firstName: { required: 'Please enter a first name.' },
    lastName: { required: 'Please enter a last name.' },
    personalEmail: {
      required: 'Please enter a personal email address.',
      email: 'Please enter a valid email address.',
      pattern: 'Please enter a valid email address.',
    },
    schoolEmail: {
      required: 'Please enter a school email address.',
      email: 'Please enter a valid school email address.',
      pattern: 'Please enter a valid school email address.',
    },
    parentEmail: {
      required: 'Please enter a parent/guardian email address.',
      email: 'Please enter a valid parent/guardian email address.',
      pattern: 'Please enter a valid parent/guardian email address.',
    },
    parentName: {
      required: 'Please enter a parent or guardian full name.',
    },
    dob: {
      required: 'Please enter a date of birth.',
      futureDob: 'Date of birth cannot be a future date.',
    },
    schoolName: { required: 'Please select a school.' },
    gender: { required: 'Please select a gender.' },
    ethnicity: { required: 'Please select an ethnicity.' },
    yearLevel: {
      required: 'Please select a year level.',
      pattern: 'Please enter a year level in numeric.',
      min: 'Please enter a year level must be at least 1',
      max: 'Please enter a year level must be 12 or less',
    },
    constructionCourse: {
      required: 'Please select Yes or No for question 1.',
    },
    constructionRelatedCourse: {
      required: 'Please select Yes or No for question 2.',
    },
  } as const;

  constructionPastAnswers: any[] = [];
  constructionCompAnswers: any[] = [];

  yearLevelOption: any[] = [];
  ethnicityOption: any[] = [];
  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<AddTeacherStudentComponent>,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: StudentDialogData,
  ) {
    this.isEdit = data?.EditMode || false;
    this.initForm();
  }

  ngOnInit(): void {
    forkJoin({
      yearLevel: this.fetchYearLevelDropDown(),
      ethnicity: this.fetchEthnicityDropDown(),
      pastAnswers: this.fetchPastAnswers(),
      compAnswers: this.fetchCompAnswers(),
    }).subscribe({
      next: () => {
        if (this.isEdit && this.data.initialData) {
          this.patchFormForEdit(this.data.initialData);
        }
        this.setupValueSubscriptions();
      },
      error: (err) => {
        console.error('Error loading dropdown data:', err);
        this.showErrorToast('Failed to load form data');
      },
    });

    this.updateValidators();
  }

  private initForm(): void {
    this.form = this.fb.group({
      firstName: [''],
      lastName: [''],
      dob: [''],
      schoolEmail: [''],
      parentName: [''],
      parentEmail: [''],
      personalEmail: [''],
      gender: [''],
      ethnicity: [''],
      yearLevel: [null],
      constructionCourse: [''],
      constructionRelatedCourse: [''],
      pastAnswers: [[]],
      completedAnswers: [[]],
    });
    this.updateValidators();
  }

  private updateValidators(): void {
    this.form.get('firstName')?.setValidators([requiredTrimmed]);
    this.form.get('lastName')?.setValidators([requiredTrimmed]);
    this.form.get('dob')?.setValidators([Validators.required, dobNotInFuture]);
    this.form
      .get('schoolEmail')
      ?.setValidators([
        Validators.required,
        Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
      ]);
    this.form.get('parentName')?.setValidators([requiredTrimmed]);
    this.form
      .get('parentEmail')
      ?.setValidators([
        Validators.required,
        Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
      ]);
    this.form
      .get('personalEmail')
      ?.setValidators([
        Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
      ]);
    this.form.get('gender')?.setValidators([requiredTrimmed]);
    this.form.get('ethnicity')?.setValidators([requiredTrimmed]);
    this.form.get('constructionCourse')?.setValidators([Validators.required]);
    this.form.get('constructionRelatedCourse')?.setValidators([Validators.required]);

    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key)?.updateValueAndValidity();
    });
  }

  onDobChange(): void {
    // const dobValue = this.form.get('dob')?.value;
    // if (dobValue) {
    //     this.calculateStudentAge(dobValue);
    //     this.updateConsentValidation();
    // } else {
    //     this.showConsentSection = false;
    //     this.isUnder16 = false;
    //     this.studentAge = null;
    // }
    // this.cdr.detectChanges();
  }

  goStepone(): void {
    this.stepSubmit = false;
  }
  private calculateStudentAge(dobString: string): void {
    try {
      const dob = new Date(dobString);
      const today = new Date();

      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < dob.getDate())
      ) {
        age--;
      }

      this.studentAge = age;
      this.isUnder16 = age < 16;
      this.showConsentSection = true;
    } catch (error) {
      console.error('Error calculating age:', error);
      this.studentAge = null;
      this.isUnder16 = false;
      this.showConsentSection = false;
    }
  }

  private updateConsentValidation(): void {
    const consentControl = this.form.get('parentGuardianConsent');
    if (this.isUnder16) {
      consentControl?.setValidators([Validators.requiredTrue]);
    } else {
      consentControl?.clearValidators();
    }

    consentControl?.updateValueAndValidity();
  }

  // private patchFormData(studentData: APIResponse): void {
  //   const selectedGenderOption = this.genderOption?.find(
  //     (option) =>
  //       option?.value?.toLowerCase() ===
  //       (studentData?.gender || '').toLowerCase(),
  //   );

  //   const selectedEthnicity = this.ethnicityOption?.find(
  //     (option) =>
  //       option?.label?.toLowerCase() ===
  //         (studentData?.ethnicity || '').toLowerCase() ||
  //       option?.value === studentData?.ethnicityId,
  //   );

  //   const selectedYearLevel = this.yearLevelOption?.find(
  //     (option) =>
  //       option?.label === studentData?.yearLevel?.toString() ||
  //       option?.value === studentData?.yearLevelId,
  //   );

  //   const formValues = {
  //     firstName: this.coalesce(studentData?.firstName, ''),
  //     lastName: this.coalesce(studentData?.lastName, ''),
  //     yearLevel: this.coalesce(
  //       selectedYearLevel?.value,
  //       studentData?.yearLevel,
  //       null,
  //     ),
  //     schoolEmail: this.coalesce(studentData?.schoolEmail, ''),
  //     dob: this.coalesce(studentData?.dateOfBirth, null),
  //     personalEmail: this.coalesce(studentData?.personalEmail, null),
  //     ethnicity: this.coalesce(
  //       selectedEthnicity?.value,
  //       studentData?.ethnicity,
  //       null,
  //     ),
  //     gender: this.coalesce(
  //       selectedGenderOption?.value,
  //       studentData?.gender,
  //       null,
  //     ),
  //     parentName: this.coalesce(studentData?.parentName, ''),
  //     parentEmail: this.coalesce(studentData?.parentEmail, ''),
  //   };

  //   const cleanFormValues = Object.entries(formValues).reduce(
  //     (acc, [key, value]) => {
  //       if (value !== undefined && value !== null) {
  //         acc[key] = value;
  //       }
  //       return acc;
  //     },
  //     {} as any,
  //   );

  //   this.form.patchValue(cleanFormValues, { emitEvent: false });
  //   Object.keys(this.form.controls).forEach((key) => {
  //     this.form.get(key)?.markAsTouched();
  //   });

  //   this.cdr.detectChanges();
  // }

  private patchFormForEdit(studentData: any): void {
    const selectedGenderOption = this.genderOption?.find(
      (option) =>
        option?.value?.toLowerCase() ===
        (studentData?.gender || '').toLowerCase(),
    );

    const selectedEthnicity = this.ethnicityOption?.find(
      (option) =>
        option?.label?.toLowerCase() ===
          (studentData?.ethnicity || '').toLowerCase() ||
        option?.value === studentData?.ethnicityId,
    );

    const selectedYearLevel = this.yearLevelOption?.find(
      (option) =>
        option?.label === studentData?.yearLevel?.toString() ||
        option?.value === studentData?.yearLevelId,
    );

    const pastCourseIds =
      studentData?.past
        ?.filter((item: any) => item.value === true)
        .map((item: any) => item.courseId) || [];
    const compCourseIds =
      studentData?.comp
        ?.filter((item: any) => item.value === true)
        .map((item: any) => item.courseId) || [];

    const hasPastCourses = pastCourseIds.length > 0;
    const hasCompCourses = compCourseIds.length > 0;

    const formValues = {
      firstName: this.coalesce(studentData?.firstName, ''),
      lastName: this.coalesce(studentData?.lastName, ''),
      yearLevel: this.coalesce(
        selectedYearLevel?.value,
        studentData?.yearLevelId,
        studentData?.yearLevel,
        null,
      ),
      schoolEmail: this.coalesce(
        studentData?.schoolEmail,
        studentData?.email,
        '',
      ),
      dob: this.coalesce(
        this.formatDateForInput(studentData?.dob),
        this.formatDateForInput(studentData?.dateOfBirth),
        null,
      ),
      personalEmail: this.coalesce(
        studentData?.personalEmail,
        studentData?.['personalEmail'],
        null,
      ),
      ethnicity: this.coalesce(
        selectedEthnicity?.value,
        studentData?.ethnicityId,
        studentData?.ethnicity,
        null,
      ),
      gender: this.coalesce(
        selectedGenderOption?.value,
        studentData?.gender,
        null,
      ),
      parentName: this.coalesce(
        studentData?.parentName,
        studentData?.parentGuardianFullName,
        '',
      ),
      parentEmail: this.coalesce(
        studentData?.parentEmail,
        studentData?.parentGuardianEmail,
        '',
      ),
      constructionCourse: hasPastCourses ? 'yes' : 'no',
      constructionRelatedCourse: hasCompCourses ? 'yes' : 'no',
      pastAnswers: pastCourseIds,
      completedAnswers: compCourseIds,
    };

    const cleanFormValues = Object.entries(formValues).reduce(
      (acc, [key, value]) => {
        if (value !== undefined && value !== null) {
          acc[key] = value;
        }
        return acc;
      },
      {} as any,
    );
    this.form.patchValue(cleanFormValues, { emitEvent: true });
    this.showConstructionCourseAnswers = hasPastCourses;
    this.showConstructionRelatedCourseAnswers = hasCompCourses;
    setTimeout(() => {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.get(key)?.updateValueAndValidity();
      });
      this.cdr.detectChanges();
    }, 0);

    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key)?.markAsTouched();
    });

    this.cdr.detectChanges();
  }

  private coalesce(...values: any[]): any {
    for (const value of values) {
      if (value !== null && value !== undefined) {
        return value;
      }
    }
    return null;
  }

  private formatDateForInput(dateValue: any): string | null {
    if (!dateValue) return null;

    // Handle DD/MM/YYYY format (e.g., "26/03/2005")
    if (typeof dateValue === 'string' && dateValue.includes('/')) {
      const parts = dateValue.split('/');
      if (parts.length === 3) {
        const [day, month, year] = parts;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }

    // Handle ISO string or Date object
    if (dateValue) {
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    }

    return null;
  }

  onSave(): void {
    if (this.isProcessing) return;
    this.markFormGroupTouched(this.form);
    const constructionCourseValue = this.form.get('constructionCourse')?.value;
    const constructionRelatedValue = this.form.get(
      'constructionRelatedCourse',
    )?.value;
    const pastAnswers = this.form.get('pastAnswers')?.value || [];
    const completedAnswers = this.form.get('completedAnswers')?.value || [];

    if (constructionCourseValue === 'yes' && pastAnswers.length === 0) {
      this.showPastAnswersError = true;
      return;
    }

    if (constructionRelatedValue === 'yes' && completedAnswers.length === 0) {
      this.showCompAnswersError = true;
      return;
    }

    if (this.form.valid) {
      this.stepSubmit = true;
      this.cdr.detectChanges();
    } else {
      this.scrollToFirstInvalidControl();
    }
  }

  onFocusAreaChange(event: any, question: 1 | 2): void {
    const value = event.target.value;
    const checked = event.target.checked;
    const controlName = question === 1 ? 'pastAnswers' : 'completedAnswers';
    const current: string[] = this.form.get(controlName)?.value ?? [];
    const updated = checked
      ? [...current, value]
      : current.filter((v) => v !== value);
    this.form.get(controlName)?.setValue(updated, { emitEvent: true });
    this.form.get(controlName)?.updateValueAndValidity();
    if (question === 1) this.showPastAnswersError = false;
    if (question === 2) this.showCompAnswersError = false;
    this.cdr.detectChanges();
  }

  onSubmit(): void {
    if (this.isProcessing) return;
    this.isProcessing = true;
    const payload = this.buildPayload();
    const studentID =
      this.data.initialData?.studentId || this.data.initialData?.id;

    const request = this.isEdit
      ? this.httpService.put<ApiDataResponse>(
          `teacherstudent/update/${studentID}`,
          payload,
        )
      : this.httpService.post<ApiDataResponse>('teacherstudent/add', payload);

    request
      .pipe(
        finalize(() => {
          this.isProcessing = false;
          this.form.enable();
        }),
      )
      .subscribe({
        next: (response) => this.handleSubmitResponse(response),
        error: (err: ApiError) => {
          this.stepSubmit = false;
          this.cdr.detectChanges();
          this.showErrorToast(
            err?.message || 'Something went wrong. Please try again later.',
          );
        },
      });
  }

  private buildPayload(): any {
    const formData = this.form.getRawValue();
    const pastAnswers = formData.pastAnswers || [];
    const completedAnswers = formData.completedAnswers || [];
    const pastCourses = this.constructionPastAnswers.map((course) => ({
      courseId: course.id,
      courseName: course.courseName,
      value:
        formData.constructionCourse === 'yes'
          ? pastAnswers.includes(course.id) || false
          : false,
    }));

    const compCourses = this.constructionCompAnswers.map((course) => ({
      courseId: course.id,
      courseName: course.courseName,
      value:
        formData.constructionRelatedCourse === 'yes'
          ? completedAnswers.includes(course.id) || false
          : false,
    }));

    const payload = {
      firstName: formData.firstName?.trim(),
      lastName: formData.lastName?.trim(),
      schoolEmail: formData.schoolEmail?.trim(),
      personalEmail: formData.personalEmail?.trim() || null,
      dateOfBirth: formData.dob,
      yearLevelId: formData.yearLevel || null,
      gender: formData.gender,
      ethnicityId: formData.ethnicity?.trim(),
      schoolId: this.data.schoolID,
      parentGuardianFullName: formData.parentName?.trim(),
      parentGuardianEmail: formData.parentEmail?.trim(),
      past: pastCourses,
      comp: compCourses,
    };

    const cleanedPayload = Object.fromEntries(
      Object.entries(payload).filter(([_, value]) => value != null),
    );

    return cleanedPayload;
  }

  private setupValueSubscriptions(): void {
    this.form.get('constructionCourse')?.valueChanges.subscribe((val) => {
      this.showConstructionCourseAnswers = val === 'yes';
      this.showPastAnswersError = false;
      if (val === 'no') {
        this.form.get('pastAnswers')?.setValue([], { emitEvent: true });
      }
      this.cdr.detectChanges();
    });

    this.form
      .get('constructionRelatedCourse')
      ?.valueChanges.subscribe((val) => {
        this.showConstructionRelatedCourseAnswers = val === 'yes';
        this.showCompAnswersError = false;
        if (val === 'no') {
          this.form.get('completedAnswers')?.setValue([], { emitEvent: true });
        }
        this.cdr.detectChanges();
      });
  }
  fetchYearLevelDropDown() {
    const type = 2;
    const status = true;

    return this.httpService
      .get<any>(`settings/yearlevel/dropdown?status=${status}`)
      .pipe(
        tap({
          next: (response) => {
            if (Array.isArray(response.data)) {
              const activeEventTypes = response.data.filter(
                (item: any) => item.status === 'Active',
              );
              this.yearLevelOption = response.data.map(
                (item: { name: any; id: any }) => ({
                  label: item.name,
                  value: item.id,
                }),
              );
            } else {
              this.showErrorToast('Invalid event type data format');
            }
          },
          error: (err) => {
            this.showErrorToast(err?.message);
          },
        }),
      );
  }

  fetchEthnicityDropDown() {
    const type = 2;
    const status = true;
    return this.httpService
      .get<any>(`settings/ethnicity/dropdown?status=${status}`)
      .pipe(
        tap({
          next: (response) => {
            if (Array.isArray(response.data)) {
              const activeEventTypes = response.data.filter(
                (item: any) => item.status === 'Active',
              );
              this.ethnicityOption = response.data.map(
                (item: { name: any; id: any }) => ({
                  label: item.name,
                  value: item.id,
                }),
              );
            } else {
              this.showErrorToast('Invalid event type data format');
            }
          },
          error: (err) => {
            this.showErrorToast(err?.message);
          },
        }),
      );
  }

  fetchCompAnswers() {
    return this.httpService.get<any>(`teacherstudent/con-rel-course-comp`).pipe(
      tap({
        next: (response) => {
          if (Array.isArray(response.data)) {
            this.constructionCompAnswers = response.data;
          } else {
            this.showErrorToast('Invalid event type data format');
          }
        },
        error: (err) => {
          this.showErrorToast(err?.message);
        },
      }),
    );
  }

  fetchPastAnswers() {
    return this.httpService.get<any>(`teacherstudent/con-rel-course-past`).pipe(
      tap({
        next: (response) => {
          if (Array.isArray(response.data)) {
            this.constructionPastAnswers = response.data;
          } else {
            this.showErrorToast('Invalid event type data format');
          }
        },
        error: (err) => {
          this.showErrorToast(err?.message);
        },
      }),
    );
  }

  private handleSubmitResponse(response: ApiDataResponse): void {
    if (response.status) {
      const message = this.isEdit
        ? 'Student updated successfully'
        : 'Student created successfully';
      this.showSuccessToast(response.message || message);
      this.dialogRef.close({ success: true, data: response.data });
    } else {
      this.stepSubmit = false;
      this.cdr.detectChanges();
      this.showErrorToast(
        response.message || 'Something went wrong. Please try again later.',
      );
    }
  }

  onCancel(): void {
    if (this.isProcessing) return;
    this.dialogRef.close(false);
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private scrollToFirstInvalidControl(): void {
    setTimeout(() => {
      const firstInvalidControl = document.querySelector(
        '.ng-invalid:not(form)',
      );
      if (firstInvalidControl) {
        firstInvalidControl.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        (firstInvalidControl as HTMLElement).focus();
      }
    }, 100);
  }

  private showToast(message: string, type: 'success' | 'error'): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: [
        'no-padding-snackbar',
        type === 'success' ? 'success-toast' : 'error-toast',
      ],
    });
  }

  private showSuccessToast(message: string): void {
    this.showToast(message, 'success');
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.showToast(message, 'error');
  }
}
